import { collection, addDoc, doc, query, where, getDocs, updateDoc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { addMonths, addYears } from 'date-fns';

export const VAT_RATE = 0.15;
export const DEFAULT_VAT_RATE = 0;

export const RECURRENCE_OPTIONS = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' }
];

export const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2
  }).format(amount || 0);
};

export const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

// Calculate VAT breakdown for a set of invoice items.
// - Exclusive: item prices are pre-VAT. total = subtotal + vat
// - Inclusive:  item prices already include VAT. subtotal (excl) is derived back out
export function calculateVat(lineItems, vatRate, taxInclusive) {
  const rawSubtotal = (lineItems || []).reduce((total, item) => {
    return total + ((Number(item.quantity) || 0) * (Number(item.price) || 0));
  }, 0);

  const rate = Number(vatRate) || 0;
  if (!rate) {
    return { subtotal: round2(rawSubtotal), vatAmount: 0, total: round2(rawSubtotal) };
  }

  if (taxInclusive) {
    const total = round2(rawSubtotal);
    const subtotal = round2(total / (1 + rate));
    return { subtotal, vatAmount: round2(total - subtotal), total };
  }

  const subtotal = round2(rawSubtotal);
  return { subtotal, vatAmount: round2(subtotal * rate), total: round2(subtotal * (1 + rate)) };
}

// Normalise a date-like value (Firestore Timestamp, Date, string, etc.)
export function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date && !isNaN(value.getTime())) return value;
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'object' && value.seconds) return new Date(value.seconds * 1000);
  return null;
}

export function advanceDate(date, frequency) {
  if (!date) return null;
  if (frequency === 'monthly') return addMonths(date, 1);
  if (frequency === 'quarterly') return addMonths(date, 3);
  if (frequency === 'yearly') return addYears(date, 1);
  return null;
}

// Copy the billable parts of an existing invoice into a fresh template.
// DateTime/payment state is intentionally not carried over.
export function buildInvoiceData(original) {
  return {
    companyName: original.companyName || '',
    companyEmail: original.companyEmail || '',
    companyPhone: original.companyPhone || '',
    companyAddress: original.companyAddress || '',
    clientName: original.clientName || original.customerName || '',
    clientEmail: original.clientEmail || original.customerEmail || '',
    clientPhone: original.clientPhone || original.customerPhone || '',
    clientAddress: original.clientAddress || original.customerAddress || '',
    items: Array.isArray(original.items)
      ? original.items.map(item => ({ description: item.description, quantity: item.quantity, price: item.price }))
      : [],
    notes: original.notes || '',
    vatRate: original.vatRate || DEFAULT_VAT_RATE,
    taxInclusive: !!original.taxInclusive,
    taxRegNo: original.taxRegNo || '',
    logoBase64: original.logoBase64 || ''
  };
}

// Reserve the next invoice number, compute totals, and persist the invoice.
export async function createInvoiceWithNumber(invoiceData) {
  if (!auth.currentUser) throw new Error('Not authenticated');

  let invoiceNumber;
  await runTransaction(db, async (transaction) => {
    const counterRef = doc(db, 'counters', 'invoices');
    const counterDoc = await transaction.get(counterRef);
    const nextNumber = counterDoc.exists() ? (counterDoc.data().count || 0) + 1 : 1;
    transaction.set(counterRef, { count: nextNumber });
    invoiceNumber = `INV-${nextNumber.toString().padStart(6, '0')}`;
  });

  const { subtotal, vatAmount, total } = calculateVat(invoiceData.items, invoiceData.vatRate, invoiceData.taxInclusive);
  const uid = auth.currentUser.uid;

  const docRef = await addDoc(collection(db, 'invoices'), {
    ...invoiceData,
    subtotal,
    vatAmount,
    total,
    status: 'pending',
    invoiceNumber,
    invoiceNo: invoiceNumber,
    userId: uid,
    createdAt: serverTimestamp(),
    customerName: invoiceData.clientName,
    customerEmail: invoiceData.clientEmail,
    customerAddress: invoiceData.clientAddress,
    customerPhone: invoiceData.clientPhone
  });

  return { id: docRef.id, invoiceNumber };
}

// "Copy to new" — creates a fresh invoice from an existing one, resetting
// status, payments and any schedule (so the copy doesn't double-bill).
export async function duplicateInvoice(original) {
  const data = buildInvoiceData(original);
  data.recurrenceFrequency = 'none';
  data.nextRunDate = null;
  return createInvoiceWithNumber(data);
}

// Generate the next invoice in a recurring series.
async function createRecurringCopy(original, runDate) {
  const data = buildInvoiceData(original);
  data.recurrenceFrequency = original.recurrenceFrequency;
  data.nextRunDate = advanceDate(runDate, original.recurrenceFrequency);
  return createInvoiceWithNumber(data);
}

// Client-side scheduler: any recurring invoice whose nextRunDate has arrived
// gets duplicated into a fresh pending invoice and the chain advances.
// Runs when the app is opened (Dashboard), so it is not a true server job.
export async function runRecurringCheck() {
  if (!auth.currentUser) return;

  let snapshot;
  try {
    const q = query(
      collection(db, 'invoices'),
      where('userId', '==', auth.currentUser.uid),
      where('recurrenceFrequency', 'in', ['monthly', 'quarterly', 'yearly'])
    );
    snapshot = await getDocs(q);
  } catch (error) {
    console.error('Recurring invoice check failed:', error);
    return;
  }

  const now = new Date();

  for (const docSnap of snapshot.docs) {
    const invoice = { id: docSnap.id, ...docSnap.data() };
    const nextRun = toDate(invoice.nextRunDate);
    if (!nextRun || nextRun > now) continue;

    try {
      const generated = await createRecurringCopy(invoice, nextRun);
      // Advance the source by two periods so the newly generated copy owns
      // the next period and the source no longer fires for another cycle.
      await updateDoc(docSnap.ref, {
        nextRunDate: advanceDate(advanceDate(nextRun, invoice.recurrenceFrequency), invoice.recurrenceFrequency)
      });
      console.log(`Generated recurring invoice ${generated.invoiceNumber}`);
    } catch (error) {
      console.error('Error generating recurring invoice:', error);
    }
  }
}